/**
 * Settings Context Provider
 *
 * Provides both general and application settings throughout the component tree.
 * Settings are loaded once at the app level and shared via context.
 *
 * General settings include: language, timezone, date/time formats, theme, accessibility
 * Application settings include: pagination, sorting, EEG defaults, auto-save preferences
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <SettingsProvider>
 *   <YourApp />
 * </SettingsProvider>
 *
 * // In any component
 * const { settings, loading } = useSettings();
 * console.log(settings.language, settings.timezone, settings.items_per_page);
 * ```
 */

import React, { createContext, useContext, ReactNode } from "react";
import {
  DEFAULT_SETTINGS,
  useAllSettings,
  type AllSettings,
} from "@shared/hooks/useAllSettings";
import type {
  ApplicationSettings,
  GeneralSettings,
} from "@features/Settings/services/SettingsService";

interface SettingsContextType {
  settings: AllSettings;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateGeneralSettings: (
    patch: Partial<GeneralSettings>,
  ) => Promise<GeneralSettings>;
  updateApplicationSettings: (
    patch: Partial<ApplicationSettings>,
  ) => Promise<ApplicationSettings>;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined,
);

interface SettingsProviderProps {
  children: ReactNode;
}

/**
 * Provider component that loads all settings once and shares them via context
 */
export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const {
    settings,
    loading,
    error,
    refetch,
    updateGeneralSettings,
    updateApplicationSettings,
  } =
    useAllSettings();

  const value: SettingsContextType = {
    settings,
    loading,
    error,
    refetch,
    updateGeneralSettings,
    updateApplicationSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to access settings from context
 * Must be used within SettingsProvider
 *
 * @throws {Error} If used outside SettingsProvider
 */
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);

  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }

  return context;
};

/**
 * Non-throwing settings accessor for legacy provider stacks (e.g. tests)
 */
export const useSettingsSafe = (): SettingsContextType => {
  return (
    useContext(SettingsContext) ?? {
      settings: DEFAULT_SETTINGS,
      loading: false,
      error: null,
      refetch: async () => undefined,
      updateGeneralSettings: async () => DEFAULT_SETTINGS,
      updateApplicationSettings: async () => DEFAULT_SETTINGS,
    }
  );
};

export { DEFAULT_SETTINGS };

export default SettingsProvider;
